import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import API from '@Services/apiAxios';
import { SHIPMENT_STATUS } from '@Constants/Shipment';

interface UpdatedBy {
  _id: string;
  name: string;
  accessGroup: string;
}

interface ShipmentChange {
  oldData?: any;
  newData?: any;
}

interface PackageChange {
  oldData?: any;
  newData?: any;
}

interface ConsigneeChange {
  oldData?: any;
  newData?: any;
}

// NEW: Shipper change interface
interface ShipperChange {
  oldData?: any;
  newData?: any;
}

interface ShipmentHistoryItem {
  _id: string;
  _shipmentId: string;
  _updatedById?: UpdatedBy;
  shipment?: ShipmentChange;
  package?: PackageChange;
  consignee?: ConsigneeChange;
  shipper?: ShipperChange; // NEW: Added shipper field
  comment?: string;
  createdAt: string;
}

interface ShipmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',

  width: {
    xs: '95%',
    sm: '90%',
    md: '700px',
  },

  maxHeight: '85vh',
  overflowY: 'auto',

  // ✅ Glass Effect
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',

  background: 'rgba(255,255,255,0.72)',

  border: '1px solid rgba(255,255,255,0.35)',

  boxShadow: `
    0 8px 32px rgba(31, 38, 135, 0.18),
    inset 0 1px 0 rgba(255,255,255,0.35)
  `,

  // ✅ Rounded Corners
  borderRadius: '24px',

  p: 3,

  // ✅ Smooth scrolling
  scrollBehavior: 'smooth',

  // ✅ Nice scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.15)',
    borderRadius: '20px',
  },
};

const formatPhone = (phone: any) => {
  if (!phone) return 'Not set';

  if (typeof phone === 'object') {
    const code = phone.telephoneCode ?? '';
    const number = phone.number ?? '';
    return `${code} ${number}`.trim();
  }

  // fallback (string)
  return phone;
};

const ShipmentHistoryModal: React.FC<ShipmentHistoryModalProps> = ({ open, onClose, shipmentId }) => {
  const [history, setHistory] = useState<ShipmentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/shipment/admin/history/${shipmentId}`);
      const data: ShipmentHistoryItem[] = response.data.data;
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch shipment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipColor = (status: string): string => {
    if (!status) return '#E0E0E0';

    switch (status.toLowerCase()) {
      case 'placed':
        return '#E6E6FF';
      case 'at warehouse':
      case 'at_warehouse':
        return '#c4c4cfff';
      case 'in transit':
      case 'in_transit':
        return '#8cc49dff';
      case 'forwarded':
        return '#B2FFC9';
      case 'rto':
        return '#AAED66';
      case 'cancelled':
        return '#e4b6b6ff';
      case 'delivered':
        return '#80a88cff';
      case 'shipment exception':
      case 'shipment_exception':
        return '#fcadadff';
      case 'unsuccessful delivery':
      case 'unsuccessful_delivery':
        return '#f1afafff';
      default:
        return '#E0E0E0';
    }
  };

  const renderPricingBreakdownChanges = (oldPricing: any[], newPricing: any[], title: string) => {
    if (!newPricing?.length) return null;

    const mapify = (arr: any[]) =>
      arr.reduce((acc, cur) => {
        acc[cur.key] = cur.value;
        return acc;
      }, {} as Record<string, number>);

    const oldMap = mapify(oldPricing || []);
    const newMap = mapify(newPricing || []);

    const keys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);

    return (
      <Paper sx={{ p: 1, mb: 2 }}>
        <Typography fontWeight={600}>{title}</Typography>
        <Divider sx={{ my: 1 }} />

        {[...keys].map(key => {
          if (oldMap[key] === newMap[key]) return null;

          return (
            <Stack key={key} direction="row" spacing={1} alignItems="center" mb={1}>
              <Typography width={160}>{key}</Typography>

              {oldMap[key] && (
                <>
                  <Chip label={oldMap[key] ?? 'NA'} size="small" variant="outlined" />

                  <Typography>→</Typography>
                </>
              )}

              <Chip label={newMap[key] ?? 'Removed'} size="small" color="success" />
            </Stack>
          );
        })}
      </Paper>
    );
  };

  const renderExtraChargesComparison = (oldCharges: any, newCharges: any, type: 'selling' | 'buying') => {
    if (!newCharges && !oldCharges) return null;

    const oldAddCharges = oldCharges?.[type]?.add || {};
    const newAddCharges = newCharges?.[type]?.add || {};

    const allChargeKeys = new Set([...Object.keys(oldAddCharges), ...Object.keys(newAddCharges)]);

    if (allChargeKeys.size === 0) return null;

    return (
      <Paper elevation={0} sx={{ p: 1, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {type === 'selling' ? 'Selling' : 'Buying'} Extra Charges
        </Typography>
        <Divider sx={{ my: 1 }} />

        {Array.from(allChargeKeys).map(key => {
          const oldValue = oldAddCharges[key]?.value;
          const newValue = newAddCharges[key]?.value;

          if (oldValue === newValue) return null;

          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{key}:</strong>
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {oldValue !== undefined ? (
                  <Chip label={`${oldValue}`} size="small" color="default" variant="outlined" />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No previous value
                  </Typography>
                )}

                <Typography variant="body2">→</Typography>

                {newValue !== undefined ? (
                  <Chip label={`${newValue}`} size="small" color={type === 'selling' ? 'secondary' : 'primary'} />
                ) : (
                  <Typography variant="caption" color="error">
                    Removed
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Paper>
    );
  };

  const renderShipmentChanges = (shipment: ShipmentChange) => {
    if (!shipment.oldData || !shipment.newData) return null;

    return (
      <>
        {renderExtraChargesComparison(shipment.oldData.extraCharges, shipment.newData.extraCharges, 'selling')}
        {renderExtraChargesComparison(shipment.oldData.extraCharges, shipment.newData.extraCharges, 'buying')}

        {renderPricingBreakdownChanges(
          shipment.oldData.sellingPricing?.pricing,
          shipment.newData.sellingPricing?.pricing,
          'Selling Pricing Breakdown',
        )}

        {renderPricingBreakdownChanges(
          shipment.oldData.buyingPricing?.pricing,
          shipment.newData.buyingPricing?.pricing,
          'Buying Pricing Breakdown',
        )}
      </>
    );
  };

  const renderPackageChanges = (pkg: PackageChange) => {
    if (!pkg.oldData || !pkg.newData) return null;

    return (
      <>
        {pkg.oldData.pickupDate && pkg.newData.pickupDate && (
          <Typography variant="body2">
            <strong>Pickup Date:</strong> {new Date(pkg.oldData.pickupDate).toLocaleString()} →{' '}
            {new Date(pkg.newData.pickupDate).toLocaleString()}
          </Typography>
        )}

        {pkg.oldData.boxWeight !== undefined && pkg.newData.boxWeight !== undefined && (
          <Typography variant="body2">
            <strong>Box Weight:</strong> {pkg.oldData.boxWeight} → {pkg.newData.boxWeight}
          </Typography>
        )}

        {pkg.oldData.box?.length && pkg.newData.box?.length && (
          <>
            <Typography variant="body2" mt={1}>
              <strong>Box Changes:</strong>
            </Typography>
            {pkg.newData.box.map((box: any, index: number) => {
              const oldBox = pkg.oldData.box[index] || {};
              return (
                <Box key={index} sx={{ pl: 2, mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Box {index + 1}:</strong> {box.childAWBNumber || 'No AWB'}
                  </Typography>
                  {oldBox.weight !== undefined && box.weight !== undefined && (
                    <Typography variant="body2">
                      <strong>Weight:</strong> {oldBox.weight} → {box.weight}
                    </Typography>
                  )}
                  {box.products?.length > 0 && (
                    <Typography variant="body2">
                      <strong>Products:</strong> {box.products.length} {box.products.length > 1 ? 'Boxes' : 'Box'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </>
    );
  };

  const renderConsigneeChanges = (consignee: ConsigneeChange) => {
    if (!consignee.oldData || !consignee.newData) return null;

    return (
      <>
        {consignee.oldData.city && consignee.newData.city && (
          <Typography variant="body2">
            <strong>City:</strong> {consignee.oldData.city} → {consignee.newData.city}
          </Typography>
        )}

        {consignee.oldData.pincode && consignee.newData.pincode && (
          <Typography variant="body2">
            <strong>Pincode:</strong> {consignee.oldData.pincode} → {consignee.newData.pincode}
          </Typography>
        )}

        {consignee.oldData.address && consignee.newData.address && (
          <Typography variant="body2">
            <strong>Address:</strong> {consignee.oldData.address} → {consignee.newData.address}
          </Typography>
        )}

        {consignee.oldData.state && consignee.newData.state && (
          <Typography variant="body2">
            <strong>State:</strong> {consignee.oldData.state} → {consignee.newData.state}
          </Typography>
        )}

        {consignee.oldData.country && consignee.newData.country && (
          <Typography variant="body2">
            <strong>Country:</strong> {consignee.oldData.country} → {consignee.newData.country}
          </Typography>
        )}
      </>
    );
  };

  // NEW: Function to render shipper changes
  const renderShipperChanges = (shipper: ShipperChange) => {
    if (!shipper.oldData || !shipper.newData) return null;

    const changes = [];

    // Check for line1 changes (from your example data)
    if (shipper.oldData.line1 !== shipper.newData.line1) {
      changes.push(
        <Typography key="line1" variant="body2">
          <strong>Line 1:</strong> {shipper.oldData.line1 || 'Not set'} → {shipper.newData.line1}
        </Typography>,
      );
    }

    // Check for other possible address fields
    const addressFields = ['line2', 'line3', 'city', 'state', 'pincode', 'country', 'address'];

    addressFields.forEach(field => {
      if (shipper.oldData[field] !== shipper.newData[field]) {
        changes.push(
          <Typography key={field} variant="body2">
            <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong> {shipper.oldData[field] || 'Not set'} →{' '}
            {shipper.newData[field]}
          </Typography>,
        );
      }
    });

    // Check for name changes
    if (shipper.oldData.name !== shipper.newData.name) {
      changes.push(
        <Typography key="name" variant="body2">
          <strong>Name:</strong> {shipper.oldData.name || 'Not set'} → {shipper.newData.name}
        </Typography>,
      );
    }

    // Check for phone/email changes
    if (
      shipper.oldData.phone?.number !== shipper.newData.phone?.number ||
      shipper.oldData.phone?.telephoneCode !== shipper.newData.phone?.telephoneCode
    ) {
      changes.push(
        <Typography key="phone" variant="body2">
          <strong>Phone:</strong> {formatPhone(shipper.oldData.phone)} → {formatPhone(shipper.newData.phone)}
        </Typography>,
      );
    }

    if (shipper.oldData.email !== shipper.newData.email) {
      changes.push(
        <Typography key="email" variant="body2">
          <strong>Email:</strong> {shipper.oldData.email || 'Not set'} → {shipper.newData.email}
        </Typography>,
      );
    }

    if (changes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No shipper changes detected
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Shipper Address Changes
        </Typography>
        {changes}
      </>
    );
  };

  const getChangeType = (item: ShipmentHistoryItem): string => {
    if (item.shipment) {
      if (item.shipment.oldData?.status && item.shipment.newData?.status) {
        return 'Status changed';
      }
      if (
        item.shipment.newData?.extraCharges ||
        item.shipment.newData?.buyingPricing ||
        item.shipment.newData?.sellingPricing
      ) {
        return 'Pricing updated';
      }
      return 'Shipment updated';
    }
    if (item.package) return 'Package';
    if (item.consignee) return 'Consignee Address Update';
    if (item.shipper) return 'Shipper Address Update'; // NEW: Added shipper case
    if (item.comment) {
      const comment = item.comment.trim().toLowerCase();
      if (comment === 'invoice created, shipment locked.') {
        return 'Invoice created';
      }
      return item.comment;
    }
    return 'Update';
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2.5}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,

            backdropFilter: 'blur(16px)',
            background: 'rgba(255,255,255,0.45)',

            borderRadius: '18px',

            px: 2,
            py: 1.5,

            border: '1px solid rgba(255,255,255,0.25)',
          }}
        >
          <Typography variant="h6">Shipment History Logs</Typography>
          <IconButton
            onClick={onClose}
            sx={{
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.45)',
              backdropFilter: 'blur(10px)',

              border: '1px solid rgba(255,255,255,0.25)',

              transition: 'all 0.25s ease',

              '&:hover': {
                transform: 'rotate(90deg)',
                background: 'rgba(255,255,255,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : history?.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              No data found
            </Typography>
          </Box>
        ) : (
          history?.map(item => (
            <Accordion
              key={item._id}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.2,
                borderRadius: '18px !important',
                overflow: 'hidden',

                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',

                border: '1px solid rgba(255,255,255,0.25)',

                boxShadow: '0 4px 18px rgba(0,0,0,0.06)',

                transition: 'all 0.25s ease',

                '&:before': {
                  display: 'none',
                },

                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="body1">{getChangeType(item)}</Typography>
                  {item.shipment?.oldData?.status && item.shipment?.newData?.status && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.oldData.status]?.label || item.shipment.oldData.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusChipColor(item.shipment.oldData.status),
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />

                      <Typography fontSize={16} variant="body2">
                        →
                      </Typography>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.newData.status]?.label || item.shipment.newData.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusChipColor(item.shipment.newData.status),
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Stack>
                  )}
                  {item.package && <Chip label="Package" size="small" color="secondary" />}
                  {item.shipment?.newData?.extraCharges && <Chip label="Extra Charges" color="primary" size="small" />}
                  {item.shipment?.newData && (
                    <>
                      <Chip label="Pricing" color="success" size="small" />
                      {(item.comment === 'Masked Shipment.' || item.comment === 'Change Service.') && (
                        <Chip label={item?.comment} color="success" size="small" />
                      )}
                    </>
                  )}
                  {item.consignee && <Chip label="Consignee Address" color="warning" size="small" />}
                  {item.shipper && <Chip label="Shipper Address" color="info" size="small" />}{' '}
                  {/* NEW: Added shipper chip */}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {item._updatedById && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Updated By:</strong> {item._updatedById.name}
                  </Typography>
                )}
                {item.comment && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Comment:</strong> {item.comment}
                  </Typography>
                )}
                {item.shipment && renderShipmentChanges(item.shipment)}
                {item.package && renderPackageChanges(item.package)}
                {item.consignee && renderConsigneeChanges(item.consignee)}
                {item.shipper && renderShipperChanges(item.shipper)} {/* NEW: Render shipper changes */}
                <Typography variant="caption" display="block" mt={1}>
                  <strong>Date:</strong> {new Date(item.createdAt).toLocaleString()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </Modal>
  );
};

export default ShipmentHistoryModal;
